import axios from "axios";
import React, { createContext, useEffect, useRef, useState } from "react";
import { API_URL, API_URL_PAYMENT, WS_URL } from "../utilities/url";
import storage from "../services/storage";
import CryptoJS from "crypto-js";
import soundMessage from "../assets/sound/notification-message.mp3";
import authService from "../services/auth.service";
import authHeader from "../services/auth.header";

export const LiveChatContext = createContext();

export const LiveChatProvider = ({ children }) => {
  const ws = useRef(null);
  const [loadingSess, setLoadingSess] = useState(false);
  const [dataDevice, setDataDevice] = useState([]);
  const [dataOSA, setDataOSA] = useState([]);
  const [OSAFilter, setOSAFilter] = useState(null);
  const [deviceFilter, setDeviceFilter] = useState(null);
  const [searchSession, setSearchSession] = useState(null);
  const [messageText, setMessageText] = useState("");
  const [messageContactText, setMessageContactText] = useState("");
  const [messageAssistantText, setMessageAssistantText] = useState("");
  const [contactASResult, setContactASResult] = useState([]);
  const [dataSession, setDataSession] = useState([]);
  const [dataMessage, setDataMessage] = useState([]);
  const [dataMessageContact, setDataMessageContact] = useState([]);
  const [dataMessageAssistant, setDataMessageAssistant] = useState([]);
  const [selectedSessionByPass, setSelectedSessionByPass] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [selectedDeviceId, setSelectedDeviceId] = useState(null);
  const [selectedFromtoId, setSelectedFromtoId] = useState(null);
  const [newMessage, setNewMessage] = useState(false);
  const [newMessageContact, setNewMessageContact] = useState(false);
  const [newMessageAssistant, setNewMessageAssistant] = useState(false);
  const selectedDeviceIdRef = useRef(selectedDeviceId);
  const selectedFromtoIdRef = useRef(selectedFromtoId);
  const [selectedSessionChatByPass, setSelectedSessionChatByPass] =
    useState(null);
  const [offsetData, setOffsetData] = useState(0);
  const [typeContactList, setTypeContactList] = useState("allchat");
  // Default lebar tengah 60%
  const [centerWidth, setCenterWidth] = useState(window.innerWidth);
  const [leftWidth, setLeftWidth] = useState(window.innerWidth * 0.16);
  const [rightWidth, setRightWidth] = useState(window.innerWidth * 0.18);
  const [centerContactWidth, setCenterContactWidth] = useState(75);

  const [isConnectedWS, setIsConnectedWS] = useState(false);
  const RECONNECT_DELAY = 3000; // Base delay before reconnecting (exponential backoff)
  const MAX_RECONNECT_ATTEMPTS = 5; // Batas maksimum reconnect
  const MAX_RECONNECT_DELAY = 30000; // Cap for the backoff delay
  const HEARTBEAT_INTERVAL = 30000; // App-level ping to keep proxies from dropping idle conns
  const STABLE_AFTER = 10000; // Only reset attempt counter once a connection survives this long
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimerRef = useRef(null);
  const heartbeatTimerRef = useRef(null);
  const stableTimerRef = useRef(null);
  const isConnectingRef = useRef(false);
  const manualCloseRef = useRef(false);

  useEffect(() => {
    setLoadingSess(true);

    const timer = setTimeout(() => {
      getLiveChatContact();
      getAssistantContact();
      getSocket();
    }, 2000);

    return () => {
      clearTimeout(timer);
      console.log("Closing WebSocket connection on unmount");
      manualCloseRef.current = true;
      clearWSTimers();
      teardownSocket();
    };
  }, []);

  const getRefreshChatbot = () => {
    getLiveChatContact();
    getAssistantContact();
  };

  useEffect(() => {
    selectedFromtoIdRef.current = selectedFromtoId;
    selectedDeviceIdRef.current = selectedDeviceId;
  }, [selectedFromtoId, selectedDeviceId]);

  // Clear every pending timer associated with the socket lifecycle.
  const clearWSTimers = () => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (heartbeatTimerRef.current) {
      clearInterval(heartbeatTimerRef.current);
      heartbeatTimerRef.current = null;
    }
    if (stableTimerRef.current) {
      clearTimeout(stableTimerRef.current);
      stableTimerRef.current = null;
    }
  };

  // Detach handlers and close the current socket so a stale socket can never
  // trigger a reconnect, and we never leave an orphaned connection open.
  const teardownSocket = () => {
    isConnectingRef.current = false;
    if (ws.current) {
      ws.current.onopen = null;
      ws.current.onclose = null;
      ws.current.onerror = null;
      ws.current.onmessage = null;
      try {
        ws.current.close();
      } catch (e) {
        // ignore
      }
      ws.current = null;
    }
  };

  const startHeartbeat = () => {
    if (heartbeatTimerRef.current) clearInterval(heartbeatTimerRef.current);
    heartbeatTimerRef.current = setInterval(() => {
      if (ws.current && ws.current.readyState === WebSocket.OPEN) {
        try {
          ws.current.send(JSON.stringify({ module: "ping" }));
        } catch (e) {
          // ignore
        }
      }
    }, HEARTBEAT_INTERVAL);
  };

  const scheduleReconnect = () => {
    if (reconnectTimerRef.current) return; // already scheduled
    if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) return; // give up
    reconnectAttemptsRef.current += 1;
    setReconnectAttempts(reconnectAttemptsRef.current);
    const delay = Math.min(
      RECONNECT_DELAY * 2 ** (reconnectAttemptsRef.current - 1),
      MAX_RECONNECT_DELAY
    );
    reconnectTimerRef.current = setTimeout(() => {
      reconnectTimerRef.current = null;
      connectSocket();
    }, delay);
  };

  // Force a clean reconnect (used on mount and on workspace switch).
  const getSocket = () => {
    clearWSTimers();
    teardownSocket();
    reconnectAttemptsRef.current = 0;
    setReconnectAttempts(0);
    connectSocket();
  };

  // Obtain a short-lived, signed ticket from otokata-api authorizing this socket.
  // The browser connects directly to otokata-core, but only with a ticket that
  // core verifies — so the (user, workspace) is authorized through the api layer.
  const fetchWSTicket = async (workspaceId) => {
    const resp = await axios.get(API_URL + "livechat/ws-ticket", {
      headers: {
        ...authHeader(),
        "X-Workspace-ID": workspaceId,
      },
    });
    return resp?.data?.data?.ticket;
  };

  const connectSocket = async () => {
    // Never open a second socket on top of a live/connecting one.
    if (isConnectingRef.current) return;
    if (
      ws.current &&
      (ws.current.readyState === WebSocket.OPEN ||
        ws.current.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }

    const decryptUSR = CryptoJS.AES.decrypt(
      storage.get("user"),
      "user"
    ).toString(CryptoJS.enc.Utf8);
    const usr = JSON.parse(decryptUSR);
    const decryptWS = CryptoJS.AES.decrypt(storage.get("wrs"), "wrs").toString(
      CryptoJS.enc.Utf8
    );
    const workspace = JSON.parse(decryptWS);

    isConnectingRef.current = true;
    manualCloseRef.current = false;

    // Fetch the connection ticket first; production core rejects the upgrade
    // without it.
    let ticket;
    try {
      ticket = await fetchWSTicket(workspace.idt_user_workspace);
    } catch (e) {
      console.error("Failed to obtain websocket ticket:", e);
    }
    // A close/teardown may have happened while awaiting the ticket.
    if (manualCloseRef.current) {
      isConnectingRef.current = false;
      return;
    }
    if (!ticket) {
      isConnectingRef.current = false;
      scheduleReconnect();
      return;
    }

    // Establish WebSocket connection. The ticket is the authoritative credential;
    // user_id/user_workspace_id are sent only for the legacy dev path on a core
    // with no service token configured (core ignores them when a token is set).
    const socket = new WebSocket(
      `${WS_URL}notification?ticket=${encodeURIComponent(ticket)}` +
        `&user_id=${usr.idt_user}&user_workspace_id=${workspace.idt_user_workspace}`
    );
    ws.current = socket;

    socket.onopen = () => {
      isConnectingRef.current = false;
      setIsConnectedWS(true);
      console.log(`WebSocket connection established: ${socket.url}`);
      startHeartbeat();
      // Only reset the attempt counter once the connection proves stable, so a
      // flapping (open -> immediate close) connection still reaches the cap.
      if (stableTimerRef.current) clearTimeout(stableTimerRef.current);
      stableTimerRef.current = setTimeout(() => {
        reconnectAttemptsRef.current = 0;
        setReconnectAttempts(0);
      }, STABLE_AFTER);
    };

    socket.onerror = (error) => {
      isConnectingRef.current = false;
      setIsConnectedWS(false);
      console.error("WebSocket error:", error);
    };

    socket.onclose = (event) => {
      isConnectingRef.current = false;
      // Ignore close events fired by a socket we've already replaced.
      if (ws.current && ws.current !== socket) return;
      setIsConnectedWS(false);
      if (heartbeatTimerRef.current) {
        clearInterval(heartbeatTimerRef.current);
        heartbeatTimerRef.current = null;
      }
      if (stableTimerRef.current) {
        clearTimeout(stableTimerRef.current);
        stableTimerRef.current = null;
      }
      ws.current = null;
      console.log("WebSocket closed:", event.code, event.reason);
      if (manualCloseRef.current) return; // intentional close, don't reconnect
      scheduleReconnect();
    };

    socket.onmessage = (event) => {
      const res = JSON.parse(event.data);
      // console.log(res);
      // Device Message
      if (res.type === "device_message") {
        if (
          res.data.fromto_id === selectedFromtoIdRef.current &&
          res.data.device_id === selectedDeviceIdRef.current
        ) {
          setDataMessage((prevMessages) => [...prevMessages, res.data]);
          setNewMessage(res.data.message_id);
        }
        // Set Session
        setDataSession((prevSessions) => {
          const sessionIndex = prevSessions.findIndex(
            (session) =>
              session.IDTDevice === res.data.device_id &&
              session.FromToID === res.data.fromto_id
          );
          const isSameAsSelectedSession =
            selectedDeviceIdRef &&
            selectedFromtoIdRef &&
            selectedDeviceIdRef.current === res.data.device_id &&
            selectedFromtoIdRef.current === res.data.fromto_id;
          if (sessionIndex !== -1) {
            // Update session if found
            const updatedSession = {
              ...prevSessions[sessionIndex],
              IDTMessage: res.data.message_id,
              Content: res.data.content ? res.data.content.slice(0, 50) : null,
              TSCreated: res.data.ts_created,
              Counter: isSameAsSelectedSession
                ? 0
                : (prevSessions[sessionIndex].Counter || 0) + 1,
            };
            // Jika Counter bertambah, mainkan suara notifikasi
            if (!isSameAsSelectedSession) {
              const audio = new Audio(soundMessage);
              audio.play().catch((err) => {
                console.error("Error playing notification sound:", err);
              });
            }
            return [
              updatedSession,
              ...prevSessions.filter((_, index) => index !== sessionIndex),
            ];
          } else {
            // Add new session if not found
            const newSession = {
              IDTDevice: res.data.device_id,
              FromToID: res.data.fromto_id,
              Direction: res.data.direction,
              IDTMessage: res.data.message_id,
              Name: res.data.name,
              DeviceName: null,
              DeviceType: res.data.channel,
              Channel: res.data.channel,
              Content: res.data.content ? res.data.content.slice(0, 50) : null,
              TSCreated: res.data.ts_created,
              Counter: 1,
            };
            return [newSession, ...prevSessions];
          }
        });
      } else if (res.type === "contact_assistant") {
        setDataMessageContact((prevMessages) => [...prevMessages, res.data]);
        setNewMessageContact(true);
      } else if (res.type === "contacts_result") {
        if (res.data.contact_id.length > 0) {
          setContactASResult(res.data.contact_id);
        } else {
          setContactASResult([-1]);
        }
        setNewMessageContact(true);
      } else if (res.type === "livechat_assistant") {
        setDataMessageAssistant((prevMessages) => [...prevMessages, res.data]);
        setNewMessageAssistant(res.data.contact_id);
      }
    };
  };

  const closeSocket = () => {
    manualCloseRef.current = true;
    clearWSTimers();
    teardownSocket();
    setIsConnectedWS(false);
    console.log("WebSocket connection closed");
  };

  // Fungsi untuk mengirim data message chat
  const sendDataMessage = (payload) => {
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify(payload));
      setMessageText("");
    } else {
      console.error("WebSocket is not open");
    }
  };

  // Fungsi untuk mengirim data message contact assistant
  const sendDataAssistantMessage = (payload) => {
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify(payload));
      setMessageAssistantText("");
    } else {
      console.error("WebSocket is not open");
    }
  };

  // Fungsi untuk mengirim data message contact assistant
  const sendDataContactMessage = (payload) => {
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify(payload));
      setMessageContactText("");
    } else {
      console.error("WebSocket is not open");
    }
  };

  const getLiveChatContact = () => {
    const decryptUSR = CryptoJS.AES.decrypt(
      storage.get("user"),
      "user"
    ).toString(CryptoJS.enc.Utf8);
    const usr = JSON.parse(decryptUSR);
    const decryptWS = CryptoJS.AES.decrypt(storage.get("wrs"), "wrs").toString(
      CryptoJS.enc.Utf8
    );
    const workspace = JSON.parse(decryptWS);
    axios
      .get(API_URL_PAYMENT + "livechat/contactsession", {
        params: {
          offset: offsetData,
          limit: 100,
          type: typeContactList,
        },
        headers: {
          "X-User-ID": usr.idt_user,
          "X-Workspace-ID": workspace.idt_user_workspace,
        },
      })
      .then((response) => {
        if (response.data.errorcode === "0000") {
          setDataOSA(response.data.data.osa ? response.data.data.osa : []);
          setDataDevice(
            response.data.data.device ? response.data.data.device : []
          );
          const updatedSessions = (
            response.data.data.message_session || []
          ).map((session) => ({
            ...session,
            Counter: 0, // Tambahkan properti Counter
          }));
          setDataSession(updatedSessions);
          setDataMessage([]);
          setSelectedSession(null);
        } else {
          setDataOSA([]);
          setDataDevice([]);
          setDataSession([]);
          setDataMessage([]);
          setSelectedSession(null);
        }
        setLoadingSess(false);
        // console.log("Response Data:", response.data);
        // Lakukan sesuatu dengan data yang diterima
      })
      .catch((error) => {
        if (error.response.data.message) {
          authService.logout();
        }
      });
  };

  const getAssistantContact = () => {
    const decryptUSR = CryptoJS.AES.decrypt(
      storage.get("user"),
      "user"
    ).toString(CryptoJS.enc.Utf8);
    const usr = JSON.parse(decryptUSR);
    const decryptWS = CryptoJS.AES.decrypt(storage.get("wrs"), "wrs").toString(
      CryptoJS.enc.Utf8
    );
    const workspace = JSON.parse(decryptWS);
    axios
      .get(API_URL_PAYMENT + "contact/assistant/session", {
        params: {
          count: 50,
          ts_until: Math.floor(Date.now() / 1000),
        },
        headers: {
          "X-User-ID": usr.idt_user,
          "X-Workspace-ID": workspace.idt_user_workspace,
        },
      })
      .then((response) => {
        if (response.data.errorcode === "0000") {
          const sortedMessages = response.data.data.messages
            ? response.data.data.messages.sort(
                (a, b) => new Date(a.ts_created) - new Date(b.ts_created)
              )
            : [];
          setDataMessageContact(
            response.data.data.messages ? sortedMessages : []
          );
          // setSelectedSession(null);
        } else {
          // setDataOSA([]);
          // setDataDevice([]);
          // setDataSession([]);
          // setDataMessage([]);
          // setSelectedSession(null);
        }
      })
      .catch((error) => {
        if (error.response.data.message) {
          authService.logout();
        }
      });
  };

  return (
    <LiveChatContext.Provider
      value={{
        centerWidth,
        setCenterWidth,
        leftWidth,
        setLeftWidth,
        rightWidth,
        setRightWidth,
        centerContactWidth,
        setCenterContactWidth,
        dataMessageContact,
        setDataMessageContact,
        dataMessageAssistant,
        setDataMessageAssistant,
        dataOSA,
        setDataOSA,
        dataDevice,
        setDataDevice,
        dataSession,
        setDataSession,
        selectedSession,
        setSelectedSession,
        selectedSessionByPass,
        setSelectedSessionByPass,
        loadingSess,
        setLoadingSess,
        searchSession,
        setSearchSession,
        messageText,
        setMessageText,
        messageContactText,
        setMessageContactText,
        messageAssistantText,
        setMessageAssistantText,
        OSAFilter,
        setOSAFilter,
        deviceFilter,
        setDeviceFilter,
        dataMessage,
        setDataMessage,
        selectedDeviceId,
        setSelectedDeviceId,
        selectedFromtoId,
        setSelectedFromtoId,
        selectedSessionChatByPass,
        setSelectedSessionChatByPass,
        newMessage,
        setNewMessage,
        newMessageContact,
        setNewMessageContact,
        newMessageAssistant,
        setNewMessageAssistant,
        getLiveChatContact,
        getAssistantContact,
        getSocket,
        closeSocket,
        getRefreshChatbot,
        sendDataMessage,
        sendDataContactMessage,
        sendDataAssistantMessage,
        contactASResult,
        setContactASResult,
        offsetData,
        setOffsetData,
        typeContactList,
        setTypeContactList,
        isConnectedWS,
        reconnectAttempts,
        setReconnectAttempts,
        connectSocket,
        reconnectAttemptsRef,
      }}
    >
      {children}
    </LiveChatContext.Provider>
  );
};
